import { useState, useEffect, useMemo, useCallback, useRef, ReactNode, ChangeEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
	ArrowLeftIcon,
	ExclamationTriangleIcon,
	CameraIcon,
	XMarkIcon,
	InformationCircleIcon,
	CheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AppLayout from '../../components/layout/AppLayout';
import { AppLoader, Alert } from '../../components/ui';
import { staffProfileApi, ApiError } from '../../api';
import type { StaffBookingSelectedTask } from '../../types/staffBookings';
import type { StaffTcrMaterialPayload } from '../../types/staffTcr';

// ─── Constants ──────────────────────────────────────────────────

const COMPLETION_OPTIONS = [
	{ value: '1', label: '1 [100%] All of it' },
	{ value: '0.75', label: '3/4 [75%] Three Quarter of it' },
	{ value: '0.5', label: '1/2 [50%] Half of it' },
	{ value: '0.33', label: '1/3 [33%] Third of it' },
	{ value: '0.25', label: '1/4 [25%] Quarter of it' },
	{ value: '0', label: '0 [0%] None of it' },
];

// Synthetic & Curly Hair options — hidden for now
// const SYNTHETIC_PACKS_OPTIONS = [
// 	{ value: '0', label: 'Select packs...' },
// 	...Array.from({ length: 8 }, (_, i) => ({ value: String(i + 1), label: `${i + 1} pack${i > 0 ? 's' : ''}` })),
// ];
// const CURLY_PACKS_OPTIONS = [
// 	{ value: '0', label: 'Select packs...' },
// 	...Array.from({ length: 6 }, (_, i) => ({ value: String(i + 1), label: `${i + 1} pack${i > 0 ? 's' : ''}` })),
// ];
// const CURLY_LENGTH_OPTIONS = [
// 	{ value: '', label: 'Select length...' },
// 	...Array.from({ length: 11 }, (_, i) => ({ value: `${10 + i * 2} inches`, label: `${10 + i * 2} inches` })),
// ];

// ─── Helpers ────────────────────────────────────────────────────

const formatCurrency = (amount: number): string =>
	new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);

const formatDate = (dateStr: string): string => {
	try {
		const date = new Date(dateStr);
		return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
	} catch {
		return dateStr;
	}
};

const formatTaskType = (taskType: string): string =>
	taskType.charAt(0).toUpperCase() + taskType.slice(1).toLowerCase();

const formatDuration = (minutes: number): string => {
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	const parts: string[] = [];
	if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
	if (mins > 0) parts.push(`${mins} minute${mins > 1 ? 's' : ''}`);
	return parts.join(' ') || '0 minutes';
};

// ─── Sub-Components ─────────────────────────────────────────────

const FormSection = ({
	title,
	icon,
	children,
}: {
	title?: string;
	icon?: ReactNode;
	children: ReactNode;
}) => (
	<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm">
		{title && (
			<h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2.5">
				{icon}
				{title}
			</h2>
		)}
		{children}
	</div>
);

const InfoCard = ({ label, value }: { label: string; value: string }) => (
	<div className="bg-gray-50 dark:bg-gray-700/50 p-3.5 rounded-xl">
		<p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
			{label}
		</p>
		<p className="text-sm font-semibold text-gray-900 dark:text-white">{value}</p>
	</div>
);

// ─── Cost Summary Card ──────────────────────────────────────────

interface CostSummaryProps {
	taskCommissions: {
		taskType: string;
		completionPct: number;
		commission: number;
		taskPrice: number;
		staffCommission: boolean;
	}[];
	totalCommission: number;
	tipAmount: number;
	totalEarnings: number;
	commissionRate: number;
}

const CostSummaryCard = ({
	taskCommissions,
	totalCommission,
	tipAmount,
	totalEarnings,
	commissionRate,
}: CostSummaryProps) => {
	return (
		<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm">
			<h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
				Detailed Cost Breakdown
			</h2>
			<div
				className="rounded-xl p-5 text-white"
				style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
			>
				{/* Per-task commission breakdown */}
				{taskCommissions.map((tc, idx) => (
					<div
						key={idx}
						className="flex justify-between items-center py-3 border-b border-white/20"
					>
						<span className="opacity-90">
							{formatTaskType(tc.taskType)}:
							<span className="text-xs opacity-80">
								{' '}
								{formatCurrency(tc.taskPrice)}
								{tc.staffCommission && <> &times; {commissionRate}%</>} &times;{' '}
								{Math.round(tc.completionPct * 100)}%
							</span>
						</span>
						<span className="font-semibold">{formatCurrency(tc.commission)}</span>
					</div>
				))}

				{/* Task breakdown text */}
				{taskCommissions.length > 1 && (
					<div className="py-2 border-b border-white/20">
						<span className="text-xs opacity-70">
							{taskCommissions
								.map(
									tc =>
										`${formatTaskType(tc.taskType)}: ${formatCurrency(tc.commission)}`
								)
								.join(' + ')}
						</span>
					</div>
				)}

				{/* = Selected Tasks Commission */}
				<div className="flex justify-between items-center py-3 border-b border-white/20">
					<span className="opacity-90">= Selected Tasks Commission</span>
					<span className="font-semibold">{formatCurrency(totalCommission)}</span>
				</div>

				{/* + Tip (100%) */}
				<div className="flex justify-between items-center py-3">
					<span className="opacity-90">+ Tip (100%)</span>
					<span className="font-semibold">{formatCurrency(tipAmount)}</span>
				</div>

				{/* Total Earnings */}
				<div className="flex justify-between items-center pt-4 mt-2 border-t-2 border-white/30 text-lg">
					<span className="font-semibold">Total Earnings</span>
					<span className="bg-white/20 px-4 py-2 rounded-lg text-xl font-bold">
						{formatCurrency(totalEarnings)}
					</span>
				</div>
			</div>
		</div>
	);
};

// ─── Service Info Shape ─────────────────────────────────────────

interface ServiceInfo {
	customerName: string;
	serviceName: string;
	size: string;
	hairLength: string;
	appointmentDateTime: string;
	amount: number;
	selectedTasks: StaffBookingSelectedTask[];
	taskMethod: string;
	taskDuration: number;
	commissionRate: number;
}

// ─── Main Component ─────────────────────────────────────────────

const TCRForm = () => {
	const navigate = useNavigate();
	const { id: tcrId } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const appointmentId = searchParams.get('appointmentId');
	const isEditMode = Boolean(tcrId);

	// Loading / submission state
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Source data (read-only)
	const [serviceInfo, setServiceInfo] = useState<ServiceInfo | null>(null);
	const [rejectionInfo, setRejectionInfo] = useState<{
		reviewNotes: string | null;
		status: string;
	} | null>(null);

	// Form fields — Materials (hidden for now)
	// const [syntheticPacks, setSyntheticPacks] = useState<number>(0);
	// const [syntheticPrice, setSyntheticPrice] = useState<string>('');
	// const [curlyPacks, setCurlyPacks] = useState<number>(0);
	// const [curlyLength, setCurlyLength] = useState<string>('');
	// const [curlyPrice, setCurlyPrice] = useState<string>('');

	// Form fields — Task completions
	const [taskCompletions, setTaskCompletions] = useState<Record<string, number>>({});

	// Form fields — Others
	const [tip, setTip] = useState<string>('');
	const [notes, setNotes] = useState<string>('');
	const [photos, setPhotos] = useState<string[]>([]);

	const fileInputRef = useRef<HTMLInputElement>(null);

	// ─── Data Fetching ────────────────────────────────────────────

	useEffect(() => {
		const loadData = async () => {
			setIsLoading(true);
			setLoadError(null);
			try {
				if (isEditMode && tcrId) {
					const tcr = await staffProfileApi.getTcrDetail(Number(tcrId));

					setServiceInfo({
						customerName: tcr.customerName,
						serviceName: tcr.serviceName,
						size: tcr.size,
						hairLength: tcr.hairLength,
						appointmentDateTime: tcr.appointmentDateTime,
						amount: tcr.amount,
						selectedTasks: tcr.selectedTasks,
						taskMethod: '',
						taskDuration: tcr.selectedTasks.reduce(
							(sum, t) => sum + t.durationMinutes,
							0
						),
						commissionRate: tcr.commissionRate,
					});

					setRejectionInfo({ reviewNotes: tcr.reviewNotes, status: tcr.status });

					// Pre-fill materials — hidden for now
					// const syntheticMat = (tcr.materials ?? []).find(m => m.materialType === 'SYNTHETIC_HAIR');
					// if (syntheticMat) { setSyntheticPacks(syntheticMat.packs); setSyntheticPrice(String(syntheticMat.price)); }
					// const curlyMat = (tcr.materials ?? []).find(m => m.materialType === 'CURLY_HAIR');
					// if (curlyMat) { setCurlyPacks(curlyMat.packs); setCurlyLength(curlyMat.length || ''); setCurlyPrice(String(curlyMat.price)); }

					// Pre-fill task completions
					const completions: Record<string, number> = {};
					(tcr.taskCompletions ?? []).forEach(tc => {
						completions[tc.taskType] = tc.completionPct;
					});
					setTaskCompletions(completions);

					setTip(tcr.tip > 0 ? String(tcr.tip) : '');
					setNotes(tcr.notes || '');

					// Lazy-load photos if available
					if (tcr.hasPhotos) {
						staffProfileApi
							.getTcrPhotos(tcr.id)
							.then(data => setPhotos(data))
							.catch(() => {
								/* silently fail */
							});
					}
				} else if (appointmentId) {
					const booking = await staffProfileApi.getBookingDetail(Number(appointmentId));

					setServiceInfo({
						customerName: booking.customerName,
						serviceName: booking.serviceName,
						size: booking.size,
						hairLength: booking.hairLength,
						appointmentDateTime: booking.appointmentDateTime,
						amount: booking.amount,
						selectedTasks: booking.selectedTasks,
						taskMethod: booking.taskMethod,
						taskDuration: booking.taskDuration,
						commissionRate:
							booking.assignedStaff?.[0]?.commissionRate ?? booking.commissionRate,
					});

					// Initialize all task completions to 100%
					const completions: Record<string, number> = {};
					booking.selectedTasks.forEach(task => {
						completions[task.taskType] = 1;
					});
					setTaskCompletions(completions);
				} else {
					setLoadError('Missing appointment ID or TCR ID');
				}
			} catch (err) {
				const apiError = err as ApiError;
				setLoadError(apiError.message || 'Failed to load data');
			} finally {
				setIsLoading(false);
			}
		};

		loadData();
	}, [isEditMode, tcrId, appointmentId]);

	// ─── Live Calculations ────────────────────────────────────────

	// Synthetic & Curly totals — hidden for now
	// const syntheticTotal = useMemo(() => (syntheticPacks > 0 && syntheticPrice ? parseFloat(syntheticPrice) || 0 : 0), [syntheticPacks, syntheticPrice]);
	// const curlyTotal = useMemo(() => (curlyPacks > 0 && curlyPrice ? parseFloat(curlyPrice) || 0 : 0), [curlyPacks, curlyPrice]);

	const taskCommissions = useMemo(() => {
		if (!serviceInfo) return [];
		const rate = serviceInfo.commissionRate / 100;
		return serviceInfo.selectedTasks.map(task => {
			const pct = taskCompletions[task.taskType] ?? 1;
			// Commission-based tasks: price × commissionRate × completion
			// Non-commission tasks: price × completion
			const commission = task.staffCommission ? task.price * rate * pct : task.price * pct;
			return {
				taskType: task.taskType,
				completionPct: pct,
				commission,
				taskPrice: task.price,
				staffCommission: !!task.staffCommission,
			};
		});
	}, [serviceInfo, taskCompletions]);

	const totalCommission = useMemo(
		() => taskCommissions.reduce((sum, tc) => sum + tc.commission, 0),
		[taskCommissions]
	);

	const tipAmount = useMemo(() => parseFloat(tip) || 0, [tip]);
	const totalEarnings = useMemo(() => totalCommission + tipAmount, [totalCommission, tipAmount]);

	// ─── Photo Handling ───────────────────────────────────────────

	const handlePhotoUpload = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files) return;

		Array.from(files).forEach(file => {
			const reader = new FileReader();
			reader.onloadend = () => {
				setPhotos(prev => [...prev, reader.result as string]);
			};
			reader.readAsDataURL(file);
		});

		if (fileInputRef.current) fileInputRef.current.value = '';
	}, []);

	const removePhoto = useCallback((index: number) => {
		setPhotos(prev => prev.filter((_, i) => i !== index));
	}, []);

	// ─── Submit ───────────────────────────────────────────────────

	const handleSubmit = async () => {
		if (!serviceInfo) return;

		setIsSubmitting(true);

		try {
			// Build materials — hidden for now, send empty array
			const materials: StaffTcrMaterialPayload[] = [];

			// Build task completions
			const taskCompletionsList = serviceInfo.selectedTasks.map(task => ({
				taskType: task.taskType,
				completionPct: taskCompletions[task.taskType] ?? 1,
			}));

			if (isEditMode && tcrId) {
				await staffProfileApi.updateTcr(Number(tcrId), {
					notes,
					taskCompletions: taskCompletionsList,
					materials,
					tip: tipAmount,
					workPhotosBase64: photos,
				});
				toast.success('TCR resubmitted successfully!');
			} else {
				await staffProfileApi.submitTcr({
					appointmentId: Number(appointmentId),
					notes,
					taskCompletions: taskCompletionsList,
					materials,
					tip: tipAmount,
					workPhotosBase64: photos,
				});
				toast.success('TCR submitted successfully!');
			}

			navigate('/staff/tcr');
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to submit TCR');
		} finally {
			setIsSubmitting(false);
		}
	};

	// ─── Render ───────────────────────────────────────────────────

	const selectClass =
		'w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors cursor-pointer';
	const inputClass =
		'w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#667eea] transition-colors';
	const readonlyInputClass =
		'w-full px-4 py-2.5 border-2 border-gray-100 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300';
	const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5';

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<button
					onClick={() => navigate(isEditMode ? '/staff/tcr' : '/staff/bookings')}
					className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-[#667eea] dark:hover:text-[#667eea] transition-colors mb-3"
				>
					<ArrowLeftIcon className="w-4 h-4" />
					{isEditMode ? 'Back to TCRs' : 'Back to Bookings'}
				</button>
				<h1 className="text-2xl font-bold text-gray-900 dark:text-white">
					{isEditMode ? 'Edit Task Completion Report' : 'Submit Task Completion Report'}
				</h1>
				{serviceInfo && (
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
						{serviceInfo.customerName} &bull; {serviceInfo.serviceName} &bull;{' '}
						{formatDate(serviceInfo.appointmentDateTime)}
					</p>
				)}
			</div>

			{/* Loading */}
			{isLoading && <AppLoader message="Loading..." variant="inline" />}

			{/* Error */}
			{loadError && !isLoading && (
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Unable to Load"
					message={loadError}
					onDismiss={() => setLoadError(null)}
				/>
			)}

			{/* Main Content */}
			{serviceInfo && !isLoading && !loadError && (
				<div className="flex gap-6 items-start flex-col xl:flex-row">
					{/* ─── Form Column ─── */}
					<div className="flex-1 min-w-0 space-y-5 w-full">
						{/* Rejection Banner */}
						{isEditMode &&
							rejectionInfo?.status === 'REJECTED' &&
							rejectionInfo.reviewNotes && (
								<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3.5">
									<ExclamationTriangleIcon className="w-6 h-6 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
									<div>
										<p className="font-semibold text-red-700 dark:text-red-400">
											This TCR was rejected
										</p>
										<p className="text-sm text-red-600 dark:text-red-300 mt-1 leading-relaxed">
											{rejectionInfo.reviewNotes}
										</p>
									</div>
								</div>
							)}

						{/* Section 1: Service Details */}
						<FormSection title="Service Details">
							<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
								<InfoCard label="Client" value={serviceInfo.customerName} />
								<InfoCard label="Service" value={serviceInfo.serviceName} />
								<InfoCard label="Size" value={serviceInfo.size || '-'} />
								<InfoCard label="Length" value={serviceInfo.hairLength || '-'} />
								<InfoCard
									label="Appointment Date"
									value={formatDate(serviceInfo.appointmentDateTime)}
								/>
								<InfoCard
									label="Service Price"
									value={formatCurrency(serviceInfo.amount)}
								/>
							</div>
						</FormSection>

						{/* Section 2 & 3: Synthetic Hair & Curly Hair — hidden for now */}

						{/* Section 4: Your Assigned Tasks & Commission */}
						<FormSection title="Your Assigned Tasks & Commission">
							{/* Read-only task info */}
							<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
								<div>
									<label className={labelClass}>Assigned Tasks</label>
									<input
										type="text"
										readOnly
										value={serviceInfo.selectedTasks
											.map(t => formatTaskType(t.taskType))
											.join(', ')}
										className={readonlyInputClass}
									/>
								</div>
								<div>
									<label className={labelClass}>Task Duration</label>
									<input
										type="text"
										readOnly
										value={formatDuration(serviceInfo.taskDuration)}
										className={readonlyInputClass}
									/>
								</div>
							</div>

							{/* Task Cards */}
							<div className="space-y-4">
								{serviceInfo.selectedTasks.map(task => {
									const pct = taskCompletions[task.taskType] ?? 1;
									const taskComm = taskCommissions.find(
										tc => tc.taskType === task.taskType
									);
									return (
										<div
											key={task.serviceTaskId}
											className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4"
										>
											<div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto] gap-4 items-end">
												{/* Task Name */}
												<div>
													<p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
														Task
														{task.staffCommission && (
															<span className="ml-1 inline-flex items-center px-1 py-0.5 rounded text-[9px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
																CR
															</span>
														)}
													</p>
													<div
														className="inline-block px-4 py-2 rounded-lg text-sm font-semibold text-white"
														style={{
															background:
																'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
														}}
													>
														{formatTaskType(task.taskType)}
													</div>
												</div>

												{/* Task Price */}
												<div className="text-center">
													<p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
														Price
													</p>
													<p className="text-sm font-semibold text-gray-900 dark:text-white">
														{formatCurrency(task.price)}
													</p>
												</div>

												{/* Completion */}
												<div>
													<p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
														Completion
													</p>
													<select
														value={String(pct)}
														onChange={e =>
															setTaskCompletions(prev => ({
																...prev,
																[task.taskType]: parseFloat(
																	e.target.value
																),
															}))
														}
														className={selectClass}
													>
														{COMPLETION_OPTIONS.map(opt => (
															<option
																key={opt.value}
																value={opt.value}
															>
																{opt.label}
															</option>
														))}
													</select>
												</div>

												{/* Commission */}
												<div className="text-right sm:text-center sm:min-w-[100px]">
													<p className="text-[10px] font-semibold text-emerald-500 dark:text-emerald-400 uppercase tracking-wider mb-1.5">
														Commission
													</p>
													<p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
														{formatCurrency(taskComm?.commission ?? 0)}
													</p>
												</div>
											</div>
										</div>
									);
								})}
							</div>

							{/* Info box */}
							<div className="flex items-center gap-2 mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
								<InformationCircleIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
								<span className="text-xs text-blue-700 dark:text-blue-300">
									Commission = Task Price &times; Completion %. Your total
									earnings include all task commissions plus any tip.
								</span>
							</div>
						</FormSection>

						{/* Section 5: Tip */}
						<FormSection>
							<label className={labelClass}>
								Tip from Customer (Only Card - Optional)
							</label>
							<div className="relative">
								<span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
									$
								</span>
								<input
									type="number"
									value={tip}
									onChange={e => setTip(e.target.value)}
									placeholder="0"
									min="0"
									step="0.01"
									className={`${inputClass} pl-8`}
								/>
							</div>
						</FormSection>

						{/* Section 6: Work Notes */}
						<FormSection title="Work Notes">
							<label className={labelClass}>Notes about the service</label>
							<textarea
								value={notes}
								onChange={e => setNotes(e.target.value)}
								placeholder="Add any notes about the service, client requests, special instructions, or observations..."
								rows={4}
								className={`${inputClass} resize-none`}
							/>
						</FormSection>

						{/* Section 7: Completed Work Photos */}
						<FormSection title="Completed Work Photos">
							<div className="flex flex-wrap gap-3">
								{photos.map((photo, idx) => (
									<div
										key={idx}
										className="relative w-24 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 group"
									>
										<img
											src={
												photo.startsWith('data:')
													? photo
													: `data:image/jpeg;base64,${photo}`
											}
											alt={`Photo ${idx + 1}`}
											className="w-full h-full object-cover"
										/>
										<button
											type="button"
											onClick={() => removePhoto(idx)}
											className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
										>
											<XMarkIcon className="w-3 h-3" />
										</button>
									</div>
								))}
								<button
									type="button"
									onClick={() => fileInputRef.current?.click()}
									className="w-24 h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-[#667eea] hover:text-[#667eea] transition-colors cursor-pointer"
								>
									<CameraIcon className="w-6 h-6" />
									<span className="text-[10px] font-medium">Add Photo</span>
								</button>
								<input
									ref={fileInputRef}
									type="file"
									accept="image/*"
									multiple
									onChange={handlePhotoUpload}
									className="hidden"
								/>
							</div>
						</FormSection>

						{/* Form Actions */}
						<div className="flex gap-3 pt-2 pb-6">
							<button
								type="button"
								onClick={() =>
									navigate(isEditMode ? '/staff/tcr' : '/staff/bookings')
								}
								className="px-6 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleSubmit}
								disabled={isSubmitting}
								className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
								style={{
									background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
								}}
							>
								{isSubmitting ? (
									<>
										<svg
											className="animate-spin w-4 h-4"
											viewBox="0 0 24 24"
											fill="none"
										>
											<circle
												className="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												strokeWidth="4"
											/>
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
											/>
										</svg>
										{isEditMode ? 'Resubmitting...' : 'Submitting...'}
									</>
								) : (
									<>
										<CheckIcon className="w-4 h-4" />
										{isEditMode ? 'Resubmit TCR' : 'Submit TCR'}
									</>
								)}
							</button>
						</div>
					</div>

					{/* ─── Summary Column ─── */}
					<div className="w-full xl:w-[350px] xl:flex-shrink-0 xl:sticky xl:top-6">
						<CostSummaryCard
							taskCommissions={taskCommissions}
							totalCommission={totalCommission}
							tipAmount={tipAmount}
							totalEarnings={totalEarnings}
							commissionRate={serviceInfo?.commissionRate ?? 0}
						/>
					</div>
				</div>
			)}
		</AppLayout>
	);
};

export default TCRForm;
